"use client"

import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import {
  getRecaptchaResponse,
  renderRecaptcha,
  resetRecaptcha,
} from "@/lib/recaptcha/client"

interface RecaptchaModalProps {
  isOpen: boolean
  onClose: () => void
  onVerify: (token: string) => void
  onError: (message: string) => void
  title?: string
}

export default function RecaptchaModal({
  isOpen,
  onClose,
  onVerify,
  onError,
  title = "Verify you are human",
}: RecaptchaModalProps) {
  const [isMounted, setIsMounted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [localToken, setLocalToken] = useState("")
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<number | null>(null)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    let cancelled = false

    if (!isOpen || !containerRef.current || widgetIdRef.current !== null) {
      return
    }

    const initRecaptcha = async () => {
      setIsLoading(true)

      try {
        const widgetId = await renderRecaptcha(containerRef.current, {
          callback: (token: string) => {
            setLocalToken(token)
          },
          "expired-callback": () => {
            setLocalToken("")
            onError("reCAPTCHA expired. Please verify again.")
          },
          "error-callback": () => {
            onError("Failed to load reCAPTCHA. Please refresh and try again.")
          },
        })

        if (!cancelled) {
          widgetIdRef.current = widgetId
        }
      } catch (_error) {
        if (!cancelled) {
          onError("Failed to initialize reCAPTCHA.")
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    initRecaptcha()

    return () => {
      cancelled = true
    }
  }, [isOpen, onError])

  useEffect(() => {
    if (!isOpen && widgetIdRef.current !== null) {
      resetRecaptcha(widgetIdRef.current)
      widgetIdRef.current = null
      setLocalToken("")
    }
  }, [isOpen])

  const handleVerify = () => {
    const token =
      localToken ||
      (widgetIdRef.current !== null
        ? getRecaptchaResponse(widgetIdRef.current)
        : "")

    if (!token) {
      onError("Please complete reCAPTCHA before final submit.")
      return
    }

    onVerify(token)
  }

  if (!isMounted || !isOpen) {
    return null
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 px-4">
      <div className="w-full max-w-md rounded bg-white p-6">
        <h3 className="mb-4 text-center text-lg font-semibold text-[#22344b]">
          {title}
        </h3>
        <div ref={containerRef} className="mb-4 flex justify-center" />
        {isLoading ? (
          <p className="mb-4 text-center text-sm text-gray-500">
            Loading reCAPTCHA...
          </p>
        ) : null}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleVerify}
            className="w-full rounded bg-[#22344b] px-4 py-2 text-sm text-white hover:bg-[#1a2837]"
          >
            Verify & Submit
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
